import useSignOut from '~/core/hooks/use-sign-out';
import useUserSession from '~/core/hooks/use-user-session';
import If from '~/core/ui/If';
import { useChatHistorySidebar } from '~/lib/contexts/ChatHistorySidebarContext';
import ProfileDropdown from './ProfileDropdown';
import SiteNavigation, { links } from './SiteNavigation';

import { Link, useLocation } from '@remix-run/react';
import { motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { footerButtonClass } from '~/components/blog/CopyToClipboard';
import FancyButton from '~/components/FancyButton';
import configuration from '~/configuration';
import { SIDEBAR_WIDTH } from '~/core/generic/helpers';
import { useLocalStorage } from '~/core/hooks/use-local-storage';
import useWindowSize from '~/core/hooks/use-window-size';
import Icon from '~/core/ui/Icon';
import LogoMini from '~/core/ui/Logo/LogoMini';
import DarkModeToggleCheckbox from './DarkModeToggleCheckbox';
import FullscreenMenu from './FullscreenMenu';

// Function to get readable path name from the current route
const getReadablePathName = (path: string) => {
  // Get the first segment of the path
  const pathSegment = path.split('/')[1];
  // replace dashes with spaces and capitalize the first letter
  const formattedSegment = pathSegment
    ? pathSegment
        .replace(/-/g, ' ')
        .replace(/\b\w/g, (char) => char.toUpperCase())
    : '';
  return formattedSegment;
};

// Define interface for link item
interface LinkItem {
  path: string;
  label: string;
}

// Dropdown Portal Component
const DropdownPortal = ({
  isOpen,
  onClose,
  anchorElement,
  links,
  pathName,
}: {
  isOpen: boolean;
  onClose: () => void;
  anchorElement: HTMLElement | null;
  links: Record<string, LinkItem>;
  pathName: string;
}) => {
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Calculate position based on the anchor element
  useEffect(() => {
    if (anchorElement && isOpen) {
      const rect = anchorElement.getBoundingClientRect();
      setPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
      });
    }
  }, [anchorElement, isOpen]);

  // Handle outside clicks
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        anchorElement &&
        !anchorElement.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    // Handle escape key press
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscapeKey);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [onClose, anchorElement]);

  if (!isOpen) return null;

  // Create portal to inject the dropdown into document body
  return createPortal(
    <div
      ref={dropdownRef}
      className="fixed z-50"
      style={{
        top: 63, //`${position.top}px`,
        left: `${position.left - 10}px`,
        zIndex: 999,
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{
          duration: 0.2,
          ease: [0.04, 0.62, 0.23, 0.98],
        }}
        className="w-fit backdrop-blur-[8px] bg-white/50 dark:bg-gray-800/50 border border-gray-400/40 dark:border-gray-700/50 rounded-lg overflow-hidden shadow-[0_0_1px_#0a0f1c01,0_14px_21px_#00218430] dark:shadow-[0_0_40px_#0a0f1c70,0_20px_40px_#08090dbd]"
      >
        <div className="p-2 space-y-1">
          {Object.values(links).map((item, index) => (
            <motion.div
              key={item.path}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{
                duration: 0.2,
                delay: index * 0.05,
                ease: [0.04, 0.62, 0.23, 0.98],
              }}
            >
              <Link
                to={item.path}
                className="block w-fit mt-[6px] mb-[9px] mr-6 text-sm font-medium text-gray-700 dark:text-gray-200 rounded-none transition-colors duration-150 border-b-2 border-transparent hover:border-[#f59e0b]"
                style={{
                  textTransform: 'uppercase',
                  fontWeight: 'bold',
                }}
                onClick={onClose}
              >
                {item.label}
              </Link>
            </motion.div>
          ))}
          {pathName && pathName.length > 0 && (
            <motion.div
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{
                duration: 0.2,
                delay: Object.values(links).length * 0.05,
                ease: [0.04, 0.62, 0.23, 0.98],
              }}
            >
              <Link
                to="/"
                className="block w-fit mt-[6px] mb-[9px] mr-6 text-sm font-medium text-gray-700 dark:text-gray-200 rounded-none transition-colors duration-150 border-b-2 border-transparent hover:border-[#f59e0b]"
                style={{
                  textTransform: 'uppercase',
                  fontWeight: 'bold',
                }}
                onClick={onClose}
              >
                Home
              </Link>
            </motion.div>
          )}
        </div>
      </motion.div>
    </div>,
    document.body,
  );
};

const SiteHeader = ({ hideNavItems = false }) => {
  const signOut = useSignOut();
  const userSession = useUserSession();
  const { width } = useWindowSize();
  const [value, setSidebarVisible] = useLocalStorage('sidebarVisible');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [wasOpenValue, setWasOpen] = useLocalStorage('wasSidebarOpen');
  const sidebarVisible = value === 'true';
  const isOnConversationsPage = false;
  const location = useLocation();
  const pathName = getReadablePathName(location.pathname);

  // State for dropdown menu visibility
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const locationRef = useRef<HTMLSpanElement>(null);

  // Use the ChatHistorySidebar context
  const { toggleSidebar: toggleHistorySidebar, isOpen } =
    useChatHistorySidebar();

  const onToggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const isOnHomePage = location.pathname === '/';

  return (
    <div
      className="site-header flex px-5 py-3 md:px-6 max-w-full md:max-w-7xl items-center justify-between fixed top-0 left-0 md:left-1/2 md:-translate-x-1/2 z-[100] w-full border-0 m-0 pl-1 pr-5 md:rounded-[1rem] backdrop-blur-[12px]"
      style={{
        width: width < 200 ? '100%' : width,
        maxWidth: width ? (width < 768 ? '100svw' : '100svw') : '100svw',
        border: 'none',
        backgroundColor: 'transparent',
      }}
    >
      <div className={`site-header-before ${isMenuOpen ? 'open' : 'closed'}`} />
      <div
        className={
          'hidden md:min-w-[33.33%] md:flex flex-1 items-center justify-start md:space-x-2 gap-1'
        }
      >
        {isOnHomePage && (
          <span
            className={
              'border-none p-2 text-black w-8 bg-transparent hover:!bg-secondary/50 rounded-lg ' +
              footerButtonClass
            }
            style={{
              display: 'flex',
              marginLeft: `${!isOpen ? 0 : SIDEBAR_WIDTH}px`,
              transition: 'margin-left 300ms ease-in-out',
              top: 10,
              left: isOpen
                ? SIDEBAR_WIDTH - 35
                : width > 768
                  ? SIDEBAR_WIDTH + 12
                  : SIDEBAR_WIDTH + 5,
              zIndex: 50,
              minWidth: '2rem',
              minHeight: '2rem',
            }}
            role={'button'}
            onClick={toggleHistorySidebar}
          >
            <Icon
              icon={'lucide:panel-left'}
              width="1.5rem"
              className="flex min-w-[1.5rem] text-black dark:text-white mt-0.5"
            />
          </span>
        )}
        {!isOpen && width > 768 && (
          <Link
            to={'/'}
            className={
              'border-none p-2 text-black w-8 bg-transparent hover:!bg-secondary/50 rounded-lg ' +
              footerButtonClass
            }
            style={{
              display: 'flex',
              transform: 'translateX(10px)',
              marginLeft: `${!isOpen ? 0 : SIDEBAR_WIDTH}px`,
              transition: 'margin-left 300ms ease-in-out',
              top: 10,
              left: SIDEBAR_WIDTH + 63,
              zIndex: 99,
              minWidth: '2rem',
              minHeight: '2rem',
            }}
            role={'button'}
          >
            <Icon
              icon="lucide:square-pen"
              width="1.5rem"
              className={
                'flex min-w-[1.5rem] mt-0.5 text-black dark:text-white'
              }
            />
          </Link>
        )}
      </div>

      {!hideNavItems && (
        <div className={'w-full flex justify-center items-center'}>
          {false && <SiteNavigation />}
          <LogoMini />
          <div className="hidden md:flex items-center relative">
            <span className="mx-2 text-gray-500">/</span>
            <span
              ref={locationRef}
              className="current-location text-sm font-medium cursor-pointer"
              style={{
                textTransform: 'uppercase',
                fontWeight: 'bold',
                display: 'flex',
                marginTop: '5px',
                borderBottom: '2px solid #f59e0b',
              }}
              onMouseEnter={() => setIsDropdownVisible(true)}
            >
              {pathName && pathName.length > 0 ? pathName : 'Home'}
            </span>
            <DropdownPortal
              isOpen={isDropdownVisible}
              onClose={() => setIsDropdownVisible(false)}
              anchorElement={locationRef.current}
              links={links}
              pathName={pathName}
            />
          </div>
        </div>
      )}
      <div
        className={
          'min-w-[50%] md:min-w-[33.33%] flex flex-1 items-center justify-end md:space-x-2 gap-1'
        }
      >
        <div className={'flex items-center'}>
          <If condition={configuration.features.enableThemeSwitcher}>
            <DarkModeToggleCheckbox />
          </If>
        </div>
        <If condition={userSession?.auth} fallback={<AuthButtons />}>
          <ProfileDropdown
            displayName={false}
            userSession={userSession}
            signOutRequested={signOut}
            className="p-1 rounded-full"
            avatarClassName="!h-[44px] md:!h-[54px] !w-[44px] md:!w-[54px] rounded-[54px]"
            noIcon
          />
        </If>

        <div className={''}>
          <FullscreenMenu links={links} onToggleMenu={onToggleMenu} />
        </div>
      </div>
    </div>
  );
};

function AuthButtons() {
  return (
    <div className={'hidden space-x-2 lg:flex whitespace-nowrap'}>
      <FancyButton
        variant={'outline'}
        href={configuration.paths.signUp}
        color={'#fff'}
        dotAmount={14}
        containerClassName="!w-full"
        className={
          '!py-3 !text-[#1f2937] dark:!text-white hover:!text-[#1f2937] dark:hover:!text-[#1f2937] !border-[#1f2937] dark:!border-white !min-w-[154px]'
        }
      >
        Get Started
      </FancyButton>
    </div>
  );
}

export default SiteHeader;
